import type { Course } from '@/payload-types'
import { richTextToPlainText } from '@/utilities/richTextToPlainText'
import { formatPriceFromEuros } from '@/utilities/formatPriceFromEuros'
import Link from 'next/link'
import Image from 'next/image'
import { ArrowRightIcon, PlayCircle, FileText, Clock, BookOpen, Gift } from 'lucide-react'
import { getClientSideURL } from '@/utilities/getURL'

type Props = {
  course: Course
  priority?: boolean
}

const CARD_IMAGE_SIZES = '(min-width: 1280px) 33vw, (min-width: 768px) 50vw, 100vw'

const pluralize = (value: number, single: string, plural: string) =>
  `${value} ${value === 1 ? single : plural}`

export const CourseCard = ({ course, priority = false }: Props) => {
  const {
    documents,
    overview,
    priceEuro,
    priceFrom,
    slug,
    title,
    summary,
    image,
    comingSoon,
    isLive,
    videoCount,
    lessonCount,
    bonusMaterials,
    totalHours,
  } = course as Course

  const hasPreview = !!isLive

  const videoCountNum = videoCount || 0
  const lessonCountNum = lessonCount || 0
  const downloadCount = documents?.length || 0
  const derivedSummary = summary || richTextToPlainText(overview, 160)
  const formattedPrice = formatPriceFromEuros(priceEuro)
  const formattedOriginalPrice = priceFrom != null ? formatPriceFromEuros(priceFrom) : null
  const discountPct =
    priceFrom != null && priceEuro != null && priceFrom > 0
      ? Math.round((1 - priceEuro / priceFrom) * 100)
      : null
  type ImageSize = {
    url?: string | null
    filename?: string | null
    width?: number | null
    height?: number | null
  }

  type UploadedLike = {
    url?: string | null
    filename?: string | null
    alt?: string | null
    focalX?: number | null
    focalY?: number | null
    sizes?: {
      thumbnail?: ImageSize | null
      card?: ImageSize | null
      large?: ImageSize | null
    } | null
  }

  const media = typeof image === 'object' ? (image as UploadedLike) : null
  const serverURL = getClientSideURL()
  const cardSize = media?.sizes?.card
  const imageURL = cardSize?.url
    ? `${serverURL}${cardSize.url}`
    : cardSize?.filename
      ? `${serverURL}/media/${cardSize.filename}`
      : media?.url
        ? `${serverURL}${media.url}`
        : media?.filename
          ? `${serverURL}/media/${media.filename}`
          : null
  const focalX = typeof media?.focalX === 'number' ? media?.focalX : 50
  const focalY = typeof media?.focalY === 'number' ? media?.focalY : 50

  return (
    <div className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border border-border/50 bg-card shadow-sm transition-all duration-300 ${hasPreview ? 'hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5' : ''}`}>
      {/* Image Section */}
      {imageURL ? (
        hasPreview ? (
          <Link href={`/cursussen/${slug}`} className="relative aspect-[16/11] overflow-hidden">
            <Image
              alt={media?.alt || title}
              className="object-cover transition-transform duration-500 group-hover:scale-105"
              src={imageURL}
              fill
              sizes={CARD_IMAGE_SIZES}
              priority={priority}
              style={{ objectPosition: `${focalX}% ${focalY}%` }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

            {!comingSoon && (discountPct != null ? (
              <div className="absolute right-4 top-4 rounded-full bg-primary px-3 py-1.5 text-sm font-bold text-primary-foreground shadow-lg">
                {discountPct}% korting
              </div>
            ) : formattedPrice ? (
              <div className="absolute right-4 top-4 rounded-full bg-white/95 px-4 py-1.5 text-sm font-bold text-foreground shadow-lg backdrop-blur-sm">
                {formattedPrice}
              </div>
            ) : null)}
          </Link>
        ) : (
          <div className="relative aspect-[16/11] overflow-hidden">
            <Image
              alt={media?.alt || title}
              className="object-cover"
              src={imageURL}
              fill
              sizes={CARD_IMAGE_SIZES}
              priority={priority}
              style={{ objectPosition: `${focalX}% ${focalY}%` }}
            />
            <div className="absolute inset-0 bg-black/40" />
            {comingSoon && (
              <div className="absolute right-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1.5 text-sm font-bold text-primary-foreground shadow-lg">
                <Clock className="h-3.5 w-3.5" />
                <span>Binnenkort beschikbaar</span>
              </div>
            )}
          </div>
        )
      ) : null}

      {/* Content Section */}
      <div className="flex flex-1 flex-col p-6">
        {/* Title */}
        {hasPreview ? (
          <Link href={`/cursussen/${slug}`} className="group/title mb-3">
            <h3 className="text-xl font-bold leading-snug text-foreground transition-colors group-hover/title:text-primary">{title}</h3>
          </Link>
        ) : (
          <h3 className="mb-3 text-xl font-bold leading-snug text-foreground">{title}</h3>
        )}

        {/* Summary */}
        {derivedSummary ? (
          <p className="mb-4 line-clamp-2 text-sm leading-relaxed text-muted-foreground">
            {derivedSummary}
          </p>
        ) : null}

        {/* Meta info */}
        {(lessonCountNum > 0 || videoCountNum > 0 || downloadCount > 0 || totalHours != null || (bonusMaterials && bonusMaterials.length > 0)) && (
          <div className="mb-5 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {totalHours != null && (
              <div className="flex items-center gap-1.5">
                <Clock className="h-4 w-4 text-primary/70" />
                <span>{totalHours} uur aan video</span>
              </div>
            )}
            {videoCountNum > 0 && (
              <div className="flex items-center gap-1.5">
                <PlayCircle className="h-4 w-4 text-primary/70" />
                <span>{pluralize(videoCountNum, 'video', "video's")}</span>
              </div>
            )}
            {lessonCountNum > 0 && (
              <div className="flex items-center gap-1.5">
                <BookOpen className="h-4 w-4 text-primary/70" />
                <span>{pluralize(lessonCountNum, 'les', 'lessen')}</span>
              </div>
            )}
            {downloadCount > 0 && (
              <div className="flex items-center gap-1.5">
                <FileText className="h-4 w-4 text-primary/70" />
                <span>{pluralize(downloadCount, 'document', 'documenten')}</span>
              </div>
            )}
            {bonusMaterials && bonusMaterials.length > 0 && (
              <div className="group/bonus relative flex items-center gap-1.5">
                <Gift className="h-4 w-4 text-primary/70" />
                <span>Bonus Materialen</span>
                <div className="pointer-events-none absolute bottom-full left-0 z-10 mb-2 w-max max-w-xs rounded-lg border border-border bg-popover p-3 text-popover-foreground opacity-0 shadow-lg transition-opacity duration-200 group-hover/bonus:pointer-events-auto group-hover/bonus:opacity-100">
                  <p className="mb-1.5 text-xs font-semibold">Bonus Materialen</p>
                  <ul className="space-y-1">
                    {bonusMaterials.map((item, index) => (
                      <li key={index} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Gift className="h-3 w-3 shrink-0 text-primary/70" />
                        {item.label}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}

        {/* CTA */}
        <div className="mt-auto flex items-center justify-between gap-3">
          {!hasPreview ? null : comingSoon ? (
            <Link
              href={`/cursussen/${slug}`}
              className="group/btn inline-flex items-center gap-2 rounded-full bg-amber-500/15 px-5 py-2.5 text-sm font-semibold text-amber-800 transition-all hover:bg-amber-500 hover:text-white"
            >
              <Clock className="h-4 w-4" />
              <span>Bekijk preview</span>
              <ArrowRightIcon
                aria-hidden
                className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5"
              />
            </Link>
          ) : (
            <Link
              href={`/cursussen/${slug}`}
              className="group/btn inline-flex items-center gap-2 rounded-full bg-primary/10 px-5 py-2.5 text-sm font-semibold text-primary transition-all hover:bg-primary hover:text-primary-foreground"
            >
              <span>Bekijk cursus</span>
              <ArrowRightIcon
                aria-hidden
                className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5"
              />
            </Link>
          )}
          {!comingSoon && formattedPrice && (
            <div className="flex flex-col items-end">
              {formattedOriginalPrice && (
                <span className="text-xs text-muted-foreground line-through">{formattedOriginalPrice}</span>
              )}
              <span className="text-base font-bold text-foreground">{formattedPrice}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

